export type FactoryBLAction = 'new' | 'edit' | 'delete' | 'copy' | 'quality' | 'print'

export type FactoryBLTabKind = 'summary' | 'list' | 'selector' | 'matching'

export interface FactoryBLColumnConfig {
  field: string
  headerName: string
  width?: number
  flex?: number
  align?: 'left' | 'right' | 'center'
  type?: 'string' | 'number' | 'date'
}

export interface FactoryBLSelectorOption {
  value: string
  label: string
  accessTarget: string
  toolbarVisible: boolean
}

export interface FactoryBLTabConfig {
  key: string
  label: string
  accessTarget: string
  endpoint: string
  kind: FactoryBLTabKind
  description: string
  sqlSource?: string
  actions: FactoryBLAction[]
  evidence: string[]
  columns: FactoryBLColumnConfig[]
  sampleRows: Array<Record<string, string | number | boolean | null>>
  selectors?: FactoryBLSelectorOption[]
}

const numeric = { align: 'right' as const, type: 'number' as const }

export const FACTORY_BL_TABS: FactoryBLTabConfig[] = [
  {
    key: 'summary',
    label: 'SUMMARY',
    accessTarget: 'frmFactorySummarySubForm2',
    endpoint: 'summary',
    kind: 'summary',
    description: 'Read-only Factory BL dashboard sourced from summary views.',
    sqlSource:
      'tlc_factory_bl_view_factory_summary_sum, tlc_factory_bl_view_factory_summary2, tlc_factory_bl_view_factory_summary3',
    actions: [],
    evidence: ['form_frmFactorySummarySubForm2.txt', 'frmFactoryQrySummary1', 'frmFactoryQrySummary2'],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'stack',
    label: 'Stack',
    accessTarget: 'frmFactoryStackSubForm',
    endpoint: 'stack',
    kind: 'list',
    description: 'Stack list filtered by date_in and soft-delete status.',
    sqlSource: 'tlc_factory_bl_view_factory_stack4',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryStackSubForm.txt', 'form_frmFactoryQryStack.txt', 'query_FactoryQryStack_baoloc.txt'],
    columns: [
      { field: 'stack_id', headerName: 'Stack ID', width: 130 },
      { field: 'date_in', headerName: 'Date In', width: 120, type: 'date' },
      { field: 'quality_code', headerName: 'Quality', width: 120 },
      { field: 'area', headerName: 'Area', width: 120 },
      { field: 'bag', headerName: 'Bag', width: 90, ...numeric },
      { field: 'net_weight', headerName: 'Net Wt', width: 120, ...numeric },
      { field: 'note', headerName: 'Note', flex: 1, width: 180 },
    ],
    sampleRows: [
      { id: 1, stack_id: 'BL-STK-2405', date_in: '2026-05-02', quality_code: 'R1', area: 'A01', bag: 320, net_weight: 19200, note: 'Open stack' },
      { id: 2, stack_id: 'BL-STK-2404', date_in: '2026-04-26', quality_code: 'R2', area: 'B03', bag: 280, net_weight: 16800, note: 'Reserved' },
    ],
  },
  {
    key: 'receive',
    label: 'Receive',
    accessTarget: 'frmFactoryReceiveSubForm',
    endpoint: 'receive',
    kind: 'list',
    description: 'GRN receive list with store, quality, copy and print workflows.',
    sqlSource: 'tlc_factory_bl_view_factory_receive5',
    actions: ['new', 'edit', 'delete', 'copy', 'quality', 'print'],
    evidence: ['form_frmFactoryReceiveSubForm.txt', 'form_frmFactoryQryReceive.txt', 'query_FactoryQryReceive_bl.txt'],
    columns: [
      { field: 'grn_id', headerName: 'GRN', width: 130 },
      { field: 'date_in', headerName: 'Date In', width: 120, type: 'date' },
      { field: 'supplier', headerName: 'Supplier', flex: 1, width: 180 },
      { field: 'order_no', headerName: 'Order No', width: 140 },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'quality_code', headerName: 'Quality', width: 110 },
      { field: 'bag', headerName: 'Bag', width: 90, ...numeric },
      { field: 'net_weight', headerName: 'Net Wt', width: 120, ...numeric },
      { field: 'deduction', headerName: 'Deduction', width: 120, ...numeric },
    ],
    sampleRows: [
      { id: 11, grn_id: 'BL-GRN-260501', date_in: '2026-05-01', supplier: 'Local Supplier A', order_no: 'PO-BL-2604', stack_id: 'BL-STK-2405', quality_code: 'R1', bag: 160, net_weight: 9600, deduction: 0.8 },
      { id: 12, grn_id: 'BL-GRN-260427', date_in: '2026-04-27', supplier: 'Internal Moving', order_no: 'MOV-2604-18', stack_id: 'BL-STK-2404', quality_code: 'R2', bag: 140, net_weight: 8400, deduction: 1.1 },
    ],
  },
  {
    key: 'issue',
    label: 'Issue',
    accessTarget: 'frmFactoryIssueSubForm',
    endpoint: 'issue',
    kind: 'list',
    description: 'Issue to production list with balance values calculated in the edit flow.',
    sqlSource: 'tlc_factory_bl_view_factory_issue2',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryIssueSubForm.txt', 'form_frmFactoryQryIssue.txt', 'query_FactoryQryIssue_baoloc.txt'],
    columns: [
      { field: 'issue_no', headerName: 'Issue No', width: 130 },
      { field: 'date', headerName: 'Date', width: 120, type: 'date' },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'category', headerName: 'Category', width: 140 },
      { field: 'issued_bag', headerName: 'Issued Bag', width: 120, ...numeric },
      { field: 'issued_nw', headerName: 'Issued NW', width: 120, ...numeric },
      { field: 'note', headerName: 'Note', flex: 1, width: 180 },
    ],
    sampleRows: [
      { id: 21, issue_no: 'BL-ISS-260501', date: '2026-05-01', stack_id: 'BL-STK-2405', category: 'Roasting', issued_bag: 80, issued_nw: 4800, note: 'Production batch P-119' },
      { id: 22, issue_no: 'BL-ISS-260429', date: '2026-04-29', stack_id: 'BL-STK-2404', category: 'Sorting', issued_bag: 60, issued_nw: 3600, note: 'Line 2' },
    ],
  },
  {
    key: 'po-list',
    label: 'Po List',
    accessTarget: 'frmFactoryMainSubForm',
    endpoint: 'po-list',
    kind: 'list',
    description: 'Factory purchase-order list; po_no is editable only when creating.',
    sqlSource: 'tlc_factory_bl_view_factory_po1',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryMainSubForm.txt', 'form_frmFactoryQryPo.txt', 'query_FactoryQryPO_baoloc.txt'],
    columns: [
      { field: 'po_no', headerName: 'PO No', width: 130 },
      { field: 'po_date', headerName: 'PO Date', width: 120, type: 'date' },
      { field: 'supplier', headerName: 'Supplier', flex: 1, width: 180 },
      { field: 'item', headerName: 'Item', width: 150 },
      { field: 'quality_code', headerName: 'Quality', width: 110 },
      { field: 'bag', headerName: 'Bag', width: 90, ...numeric },
      { field: 'net_weight', headerName: 'Net Wt', width: 120, ...numeric },
      { field: 'status', headerName: 'Status', width: 120 },
    ],
    sampleRows: [
      { id: 31, po_no: 'PO-BL-2604', po_date: '2026-04-25', supplier: 'Local Supplier A', item: 'Robusta', quality_code: 'R1', bag: 500, net_weight: 30000, status: 'Open' },
      { id: 32, po_no: 'PO-BL-2603', po_date: '2026-03-18', supplier: 'Local Supplier B', item: 'Robusta', quality_code: 'R2', bag: 350, net_weight: 21000, status: 'Closed' },
    ],
  },
  {
    key: 'delivery',
    label: 'Delivery',
    accessTarget: 'frmFactoryDeliverySubForm',
    endpoint: 'delivery',
    kind: 'list',
    description: 'GDN delivery list with quality, copy and print-preview flows.',
    sqlSource: 'tlc_factory_bl_view_factory_delivery1',
    actions: ['new', 'edit', 'delete', 'copy', 'quality', 'print'],
    evidence: ['form_frmFactoryDeliverySubForm.txt', 'form_frmFactoryQryDelivery.txt', 'query_FactoryQryDelivery_baoloc.txt'],
    columns: [
      { field: 'gdn_id', headerName: 'GDN', width: 130 },
      { field: 'date_out', headerName: 'Date Out', width: 120, type: 'date' },
      { field: 'customer', headerName: 'Customer', flex: 1, width: 180 },
      { field: 'contract_no', headerName: 'Contract', width: 150 },
      { field: 'lot_id', headerName: 'Lot', width: 130 },
      { field: 'bag', headerName: 'Bag', width: 90, ...numeric },
      { field: 'net_weight', headerName: 'Net Wt', width: 120, ...numeric },
      { field: 'quality_status', headerName: 'QC', width: 110 },
    ],
    sampleRows: [
      { id: 41, gdn_id: 'BL-GDN-260502', date_out: '2026-05-02', customer: 'Export Customer A', contract_no: 'S-2604-019', lot_id: 'BL-LOT-2407', bag: 120, net_weight: 7200, quality_status: 'Passed' },
      { id: 42, gdn_id: 'BL-GDN-260426', date_out: '2026-04-26', customer: 'Local Customer B', contract_no: 'LS-2604-011', lot_id: 'BL-LOT-2406', bag: 90, net_weight: 5400, quality_status: 'Pending' },
    ],
  },
  {
    key: 'consumables',
    label: 'Consumables',
    accessTarget: 'frmFactoryConsumablesSubForm',
    endpoint: 'consumables',
    kind: 'list',
    description: 'MRN/MDN consumables list; copy clears MRN/MDN number and category.',
    sqlSource: 'tlc_factory_bl_view_factory_consumables1',
    actions: ['new', 'edit', 'delete', 'copy'],
    evidence: ['form_frmFactoryConsumablesSubForm.txt', 'form_frmFactoryQryConsumables.txt', 'FactoryQryConsumables_all'],
    columns: [
      { field: 'mrn_mdn_no', headerName: 'MRN/MDN', width: 130 },
      { field: 'mrn_or_mdn', headerName: 'Type', width: 90 },
      { field: 'date', headerName: 'Date', width: 120, type: 'date' },
      { field: 'category', headerName: 'Category', width: 140 },
      { field: 'item_name', headerName: 'Item', flex: 1, width: 180 },
      { field: 'qty', headerName: 'Qty', width: 90, ...numeric },
      { field: 'uom', headerName: 'UOM', width: 90 },
      { field: 'note', headerName: 'Note', flex: 1, width: 180 },
    ],
    sampleRows: [
      { id: 51, mrn_mdn_no: 'MRN-260501', mrn_or_mdn: 'MRN', date: '2026-05-01', category: 'Packaging', item_name: 'Jute bag', qty: 500, uom: 'pcs', note: 'Receipt' },
      { id: 52, mrn_mdn_no: 'MDN-260427', mrn_or_mdn: 'MDN', date: '2026-04-27', category: 'Fuel', item_name: 'Diesel', qty: 120, uom: 'litre', note: 'Line 1' },
    ],
  },
  {
    key: 'lot-allocation',
    label: 'Lot Allocation',
    accessTarget: 'frmFactoryLotAllocationSubForm',
    endpoint: 'lot-allocation',
    kind: 'list',
    description: 'Lot-to-GRN allocation list with stack and GRN balances.',
    sqlSource: 'tlc_factory_bl_view_factory_lot_allocation2',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryLotAllocationSubForm.txt', 'form_frmFactoryQryLotAllocation.txt', 'query_FactoryQryLotAllocation_baoloc.txt'],
    columns: [
      { field: 'lot_id', headerName: 'Lot', width: 130 },
      { field: 'date_check', headerName: 'Date Check', width: 120, type: 'date' },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'grn_id', headerName: 'GRN', width: 130 },
      { field: 'allocated_bag', headerName: 'Alloc Bag', width: 120, ...numeric },
      { field: 'allocated_nw', headerName: 'Alloc NW', width: 120, ...numeric },
      { field: 'balance_nw', headerName: 'Balance NW', width: 130, ...numeric },
    ],
    sampleRows: [
      { id: 61, lot_id: 'BL-LOT-2407', date_check: '2026-05-02', stack_id: 'BL-STK-2405', grn_id: 'BL-GRN-260501', allocated_bag: 120, allocated_nw: 7200, balance_nw: 2400 },
      { id: 62, lot_id: 'BL-LOT-2406', date_check: '2026-04-25', stack_id: 'BL-STK-2404', grn_id: 'BL-GRN-260427', allocated_bag: 90, allocated_nw: 5400, balance_nw: 3000 },
    ],
  },
  {
    key: 'lot-mgt',
    label: 'Lot Mgt',
    accessTarget: 'frmFactoryLotMgtSubForm',
    endpoint: 'lot-mgt',
    kind: 'list',
    description: 'Lot management list; new/copy suggests a fresh lot_id.',
    sqlSource: 'tlc_factory_bl_view_factory_lot_mgt1',
    actions: ['new', 'edit', 'delete', 'copy'],
    evidence: ['form_frmFactoryLotMgtSubForm.txt', 'form_frmFactoryQryLotMgt.txt', 'query_FactoryQryLotMgt_baoloc.txt'],
    columns: [
      { field: 'lot_id', headerName: 'Lot', width: 130 },
      { field: 'date_check', headerName: 'Date Check', width: 120, type: 'date' },
      { field: 'quality_code', headerName: 'Quality', width: 110 },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'bag', headerName: 'Bag', width: 90, ...numeric },
      { field: 'net_weight', headerName: 'Net Wt', width: 120, ...numeric },
      { field: 'status', headerName: 'Status', width: 120 },
    ],
    sampleRows: [
      { id: 71, lot_id: 'BL-LOT-2407', date_check: '2026-05-02', quality_code: 'R1', stack_id: 'BL-STK-2405', bag: 120, net_weight: 7200, status: 'Ready' },
      { id: 72, lot_id: 'BL-LOT-2406', date_check: '2026-04-25', quality_code: 'R2', stack_id: 'BL-STK-2404', bag: 90, net_weight: 5400, status: 'Open' },
    ],
  },
  {
    key: 'quality-standard',
    label: 'Quality Standard',
    accessTarget: 'frmFactoryDeductionStandardSubForm',
    endpoint: 'quality-standard',
    kind: 'list',
    description: 'Factory deduction-standard quality rows for Bao Loc.',
    sqlSource: 'tlc_factory_view_factory_deduction_standard_quality',
    actions: ['edit', 'delete'],
    evidence: ['form_frmFactoryDeductionStandardSubForm.txt', 'query_FactoryQryDeductionStandard_baoloc.txt'],
    columns: [
      { field: 'parameter_code', headerName: 'Parameter', width: 140 },
      { field: 'parameter_name', headerName: 'Name', flex: 1, width: 180 },
      { field: 'sort_txt', headerName: 'Sort', width: 100 },
      { field: 'min_value', headerName: 'Min', width: 90, ...numeric },
      { field: 'max_value', headerName: 'Max', width: 90, ...numeric },
      { field: 'deduction_basis', headerName: 'Basis', width: 160 },
    ],
    sampleRows: [
      { id: 81, parameter_code: 'MC', parameter_name: 'Moisture', sort_txt: '01', min_value: 0, max_value: 12.5, deduction_basis: 'Standard' },
      { id: 82, parameter_code: 'FM', parameter_name: 'Foreign Matter', sort_txt: '02', min_value: 0, max_value: 1, deduction_basis: 'Standard' },
    ],
  },
  {
    key: 'deduction-rate',
    label: 'Deduction Rate',
    accessTarget: 'frmFactoryDeductionRealSubForm',
    endpoint: 'deduction-rate',
    kind: 'list',
    description: 'Factory deduction-real quality rows for Bao Loc.',
    sqlSource: 'tlc_factory_view_factory_deduction_real_quality',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryDeductionRealSubForm.txt', 'query_FactoryQryDeductionReal_baoloc.txt'],
    columns: [
      { field: 'parameter_code', headerName: 'Parameter', width: 140 },
      { field: 'field_value', headerName: 'Value', width: 110, ...numeric },
      { field: 'check_edit', headerName: 'Editable', width: 110 },
      { field: 'rate', headerName: 'Rate', width: 110, ...numeric },
      { field: 'sort_txt', headerName: 'Sort', width: 100 },
      { field: 'note', headerName: 'Note', flex: 1, width: 180 },
    ],
    sampleRows: [
      { id: 91, parameter_code: 'MC', field_value: 13, check_edit: 'Yes', rate: 0.5, sort_txt: '01', note: 'Over standard' },
      { id: 92, parameter_code: 'FM', field_value: 2, check_edit: 'Yes', rate: 1, sort_txt: '02', note: 'Foreign matter' },
    ],
  },
  {
    key: 'allocating-s-stack',
    label: 'Allocating: S - Stack',
    accessTarget: 'frmMatchingPSMatch_WH_Stock_Sales',
    endpoint: 'allocating-s-stack',
    kind: 'matching',
    description: 'Matching workspace filtered to factory_name=baoloc.',
    sqlSource: 'public_v_match_s_stock, public_v_matching_inventory',
    actions: [],
    evidence: ['form_frmMatchingPSMatch_WH_Stock_Sales.txt'],
    columns: [],
    sampleRows: [],
  },
  {
    key: 'data',
    label: 'Data',
    accessTarget: 'frmFactoryMainSubFormMD',
    endpoint: 'data',
    kind: 'selector',
    description: 'Metadata selector workspace; toolbar depends on selected dataset.',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryMainSubFormMD.txt'],
    columns: [
      { field: 'dataset', headerName: 'Dataset', width: 180 },
      { field: 'code', headerName: 'Code', width: 140 },
      { field: 'name', headerName: 'Name', flex: 1, width: 220 },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'updated_by', headerName: 'Updated By', width: 140 },
    ],
    selectors: [
      { value: 'item-list', label: 'Item List', accessTarget: 'frmMdQryValuation', toolbarVisible: false },
      { value: 'bom', label: 'BOM', accessTarget: 'frmFactoryMDQryBOM', toolbarVisible: true },
      { value: 'category-material', label: 'Category Material', accessTarget: 'frmFactoryMDQryMaterial', toolbarVisible: true },
      { value: 'movement-holidays', label: 'Movement - Holidays', accessTarget: 'frmFactoryMDQryHolidays', toolbarVisible: true },
      { value: 'movement-kpis', label: 'Movement - KPIs', accessTarget: 'frmFactoryMDQryMovementKPI', toolbarVisible: true },
      { value: 'matching-scontract', label: 'Matching - SContract', accessTarget: 'frmFactoryMDMatchingLocalS_baoloc', toolbarVisible: false },
      { value: 'matching-pcontract', label: 'Matching - PContract', accessTarget: 'frmFactoryMDMatchingLocalP_baoloc', toolbarVisible: false },
    ],
    sampleRows: [
      { id: 101, dataset: 'BOM', code: 'BOM-ROBUSTA', name: 'Robusta processing BOM', status: 'Editable', updated_by: 'system' },
      { id: 102, dataset: 'Movement - KPIs', code: 'KPI-DAILY', name: 'Daily movement target', status: 'Editable', updated_by: 'system' },
    ],
  },
  {
    key: 'movements',
    label: 'Movements',
    accessTarget: 'frmFactoryMovementMainSubForm',
    endpoint: 'movements',
    kind: 'selector',
    description: 'Movement selector workspace with report modes and input modes.',
    actions: ['new', 'edit', 'delete'],
    evidence: ['form_frmFactoryMovementMainSubForm.txt'],
    columns: [
      { field: 'dataset', headerName: 'Dataset', width: 190 },
      { field: 'input_date', headerName: 'Input Date', width: 120, type: 'date' },
      { field: 'lot_id', headerName: 'Lot', width: 130 },
      { field: 'stack_id', headerName: 'Stack', width: 130 },
      { field: 'moving_weight', headerName: 'Moving Wt', width: 120, ...numeric },
      { field: 'moving_bag', headerName: 'Moving Bag', width: 120, ...numeric },
      { field: 'moving_area', headerName: 'Moving Area', flex: 1, width: 160 },
    ],
    selectors: [
      { value: 'internal-moving', label: 'Internal Moving', accessTarget: 'frmFactoryMovementQryInternalMoving', toolbarVisible: true },
      { value: 'report-base-goal-real', label: 'Report - Base Goal&Real', accessTarget: 'frmFactoryQryMovementReport1', toolbarVisible: false },
      { value: 'report-activities', label: 'Report - Activities', accessTarget: 'frmFactoryQryMovementReport_Movement_baoloc', toolbarVisible: false },
      { value: 'input-daily', label: 'Input Daily (Goal&Real)', accessTarget: 'frmFactoryMovementQryDaily_baoloc', toolbarVisible: true },
      { value: 'input-monthly', label: 'Input Monthly (Goal&Real)', accessTarget: 'frmFactoryMovementQryMonthly', toolbarVisible: true },
    ],
    sampleRows: [
      { id: 111, dataset: 'Internal Moving', input_date: '2026-05-02', lot_id: 'BL-LOT-2407', stack_id: 'BL-STK-2405', moving_weight: 7200, moving_bag: 120, moving_area: 'A01 -> Loading' },
      { id: 112, dataset: 'Input Daily (Goal&Real)', input_date: '2026-05-01', lot_id: '', stack_id: '', moving_weight: 18600, moving_bag: 310, moving_area: 'Daily input' },
    ],
  },
]

export type FactoryBLTabKey = (typeof FACTORY_BL_TABS)[number]['key']

export const DEFAULT_FACTORY_BL_TAB = 'summary'

export function isFactoryBLTabKey(value: string | undefined): value is FactoryBLTabKey {
  return FACTORY_BL_TABS.some((tab) => tab.key === value)
}
